import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ApiError } from '../ApiError';
import { get, post, type ApiData } from '../typed';

/**
 * Hooks do shell (T2) — o que a moldura do console consulta em toda navegação:
 * quem é o usuário, o estado do executor, os contadores do menu e o sino.
 *
 * Cada tela T* traz os seus em `src/app/main/<Module>/<Module>.api.ts`.
 */

export type Me = ApiData<'/api/v1/auth/me'>;
export type Shell = ApiData<'/api/v1/overview/shell'>;
export type Health = ApiData<'/health'>;
export type ApprovalSummary = ApiData<'/api/v1/approvals/summary'>;
export type SearchResult = ApiData<'/api/v1/search'>;

/** Chaves de cache do shell — invalidação e limpeza no logout usam estas. */
export const shellKeys = {
  me: ['auth', 'me'] as const,
  shell: ['overview', 'shell'] as const,
  health: ['health'] as const,
  approvalsSummary: ['approvals', 'summary'] as const,
  busca: (term: string) => ['search', term] as const,
};

/** Mínimo de caracteres da busca global — o contrato recusa menos que isto. */
export const SEARCH_MIN = 2;

/** Usuário da sessão + tenant da topbar (T2, FR-015). */
export function useMe(): UseQueryResult<Me, ApiError> {
  return useQuery<Me, ApiError>({
    queryKey: shellKeys.me,
    queryFn: () => get('/api/v1/auth/me'),
    // Sessão expirada não se resolve repetindo: o guard leva a T1 (FR-007).
    retry: false,
  });
}

/** Chip do middleware e contadores do menu (T2, FR-012, FR-013). */
export function useShell(): UseQueryResult<Shell, ApiError> {
  return useQuery<Shell, ApiError>({
    queryKey: shellKeys.shell,
    queryFn: () => get('/api/v1/overview/shell'),
    // O chip do middleware precisa envelhecer rápido (FR-125).
    staleTime: 15_000,
  });
}

/** Contador do sino da topbar (T2 → T9, FR-013). */
export function useApprovalsSummary(): UseQueryResult<ApprovalSummary, ApiError> {
  return useQuery<ApprovalSummary, ApiError>({
    queryKey: shellKeys.approvalsSummary,
    queryFn: () => get('/api/v1/approvals/summary'),
    staleTime: 15_000,
  });
}

/**
 * Busca global da topbar (T2, FR-014). Só chama a partir de `BUSCA_MIN`
 * caracteres — abaixo disso o contrato responde 422 e não há o que mostrar.
 */
export function useSearchGlobal(term: string): UseQueryResult<SearchResult, ApiError> {
  const q = term.trim();
  return useQuery<SearchResult, ApiError>({
    queryKey: shellKeys.busca(q),
    queryFn: () => get('/api/v1/search', { params: { q } }),
    enabled: q.length >= SEARCH_MIN,
    staleTime: 30_000,
  });
}

/** Liveness do backend (T1, FR-011). */
export function useHealth(): UseQueryResult<Health, ApiError> {
  return useQuery<Health, ApiError>({
    queryKey: shellKeys.health,
    queryFn: () => get('/health'),
    retry: false,
  });
}

/**
 * **Sair** do menu do usuário (FR-008).
 *
 * O cookie é httpOnly: quem o expira é o backend. Aqui só limpamos o cache —
 * nenhum dado de uma sessão pode sobreviver para a próxima — e quem chama
 * decide para onde navegar (`/login`).
 */
export function useLogout(): UseMutationResult<unknown, ApiError, void> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, void>({
    mutationFn: () => post('/api/v1/auth/logout'),
    // Mesmo se o backend recusar, a intenção do usuário foi sair: o cache vai
    // embora nos dois caminhos e o guard de sessão decide o resto.
    onSettled: () => queryClient.clear(),
  });
}
